import { Injectable } from '@angular/core';
import { finalize, take } from 'rxjs/operators';

import { ReaImportDataPresenter } from '../presenters/rea-import-data.presenter';

import { ReaImportService } from '../../../../../service/fincas/rea-import.service';

import { BaseResponseModel } from 'src/app/models/base-response.model';
import {
  HistoricoREAModel,
  ReaAuditData,
  ReaAuditSummaryItem,
  ReaImportAuditDetail,
  ReaImportAuditDetailResponse,
  ReaImportAuditRecord,
  ReaImportAuditStats,
} from 'src/app/models/fincas/rea.model';

/**
 * Keeps audit-related state and API calls outside the modal component.
 */
@Injectable()
export class ReaImportAuditFacade {
  public showPanel = false;

  public auditHistory: BaseResponseModel<ReaImportAuditRecord> | null = null;

  public auditDetailResult: BaseResponseModel<ReaAuditData> | null = null;

  public auditStats: BaseResponseModel<ReaImportAuditStats> | null = null;

  public selectedImportId = '';

  public auditLoading = false;

  public detailLoading = false;

  public statsLoading = false;

  private readonly auditHistoryLimit = 5;

  private readonly auditStatsLimit = 30;

  private readonly reaCodeRegex = /^ES\d{12}$/;

  private lastHistoryCodigoRea = '';

  private activeAuditCodigoRea = '';

  constructor(private readonly reaImportService: ReaImportService) {}

  public get records(): ReaImportAuditRecord[] {
    return this.auditHistory?.data ?? [];
  }

  public get hasHistory(): boolean {
    return this.records.length > 0;
  }

  public get statsRecord(): ReaImportAuditStats | null {
    return ReaImportDataPresenter.auditStats(this.auditStats);
  }

  public get statsMetrics(): ReaAuditSummaryItem[] {
    const stats = this.statsRecord;

    if (stats == null || !stats.available) {
      return [];
    }

    return [
      { label: 'Importaciones', value: stats.importsTotal },
      { label: 'Correctas', value: stats.successfulImports },
      { label: 'Con avisos', value: stats.warningImports },
      { label: 'Fallidas', value: stats.failedImports },
      { label: 'Parcelas procesadas', value: stats.totals.parcelasDetectadas },
      { label: 'Cambios acumulados', value: stats.totals.parcelasConCambios },
    ];
  }

  public get selectedDetail(): ReaImportAuditDetailResponse | null {
    return this.reaImportService.normalizeAuditDetailResponse(this.auditDetailResult?.firstOrNull() ?? null);
  }

  public get selectedChanges(): ReaImportAuditDetail[] {
    return this.selectedDetail?.details ?? [];
  }

  public reset(): void {
    this.resetHistory();
    this.lastHistoryCodigoRea = '';
  }

  public resetHistory(): void {
    this.showPanel = false;
    this.auditHistory = null;
    this.auditDetailResult = null;
    this.auditStats = null;
    this.selectedImportId = '';
    this.auditLoading = false;
    this.detailLoading = false;
    this.statsLoading = false;
    this.activeAuditCodigoRea = '';
  }

  public closePanel(): void {
    this.showPanel = false;
  }

  public togglePanel(): void {
    this.showPanel = !this.showPanel;
  }

  public loadLastRegisterRea(
    codigoRea: string,
    currentHistorico: BaseResponseModel<HistoricoREAModel> | null,
    updateHistorico: (response: BaseResponseModel<HistoricoREAModel>) => void,
    notifyChange: () => void,
  ): void {
    const normalizedRea = codigoRea.trim();

    if (!this.isValidReaCode(normalizedRea)) {
      return;
    }

    if (normalizedRea === this.lastHistoryCodigoRea && currentHistorico != null) {
      return;
    }

    this.lastHistoryCodigoRea = normalizedRea;

    this.reaImportService.getLastRegisterRea(normalizedRea).pipe(take(1)).subscribe((response) => {
      updateHistorico(response);
      notifyChange();
    });
  }

  public loadHistory(codigoRea: string, notifyChange: () => void): void {
    const normalizedRea = codigoRea.trim();

    if (!this.isValidReaCode(normalizedRea)) {
      return;
    }

    if (this.auditLoading && this.activeAuditCodigoRea === normalizedRea) {
      return;
    }

    this.activeAuditCodigoRea = normalizedRea;
    this.auditHistory = null;
    this.auditDetailResult = null;
    this.auditStats = null;
    this.selectedImportId = '';
    this.auditLoading = true;
    notifyChange();

    this.reaImportService.getImportAuditHistory(normalizedRea, this.auditHistoryLimit).pipe(
      take(1),
      finalize(() => {
        if (this.activeAuditCodigoRea !== normalizedRea) {
          return;
        }

        this.auditLoading = false;
        notifyChange();
      }),
    ).subscribe((response) => {
      if (this.activeAuditCodigoRea !== normalizedRea) {
        return;
      }

      this.auditHistory = response.isOk ? response : null;
      this.loadStats(normalizedRea, notifyChange);
      notifyChange();
    });
  }

  public loadStats(codigoRea: string, notifyChange: () => void): void {
    const normalizedRea = codigoRea.trim();

    if (!this.isValidReaCode(normalizedRea)) {
      return;
    }

    if (this.statsLoading && this.activeAuditCodigoRea === normalizedRea) {
      return;
    }

    this.statsLoading = true;
    notifyChange();

    this.reaImportService.getImportAuditStats(normalizedRea, this.auditStatsLimit).pipe(
      take(1),
      finalize(() => {
        if (this.activeAuditCodigoRea !== normalizedRea) {
          return;
        }

        this.statsLoading = false;
        notifyChange();
      }),
    ).subscribe((response) => {
      if (this.activeAuditCodigoRea !== normalizedRea) {
        return;
      }

      this.auditStats = response.isOk ? response : null;
      notifyChange();
    });
  }

  public showDetail(record: ReaImportAuditRecord, notifyChange: () => void): void {
    const importId = record.importId.trim();

    if (importId === '') {
      return;
    }

    if (this.selectedImportId === importId) {
      this.closeDetail();
      notifyChange();
      return;
    }

    if (this.detailLoading) {
      return;
    }

    this.selectedImportId = importId;
    this.detailLoading = true;
    this.auditDetailResult = null;
    notifyChange();

    this.reaImportService.getImportAuditDetail(importId).pipe(
      take(1),
      finalize(() => {
        this.detailLoading = false;
        notifyChange();
      }),
    ).subscribe((response) => {
      if (this.selectedImportId !== importId) {
        return;
      }

      this.auditDetailResult = response.isOk ? response : null;
      notifyChange();
    });
  }

  public closeDetail(): void {
    this.auditDetailResult = null;
    this.selectedImportId = '';
  }

  private isValidReaCode(reaCode: string): boolean {
    return this.reaCodeRegex.test(reaCode);
  }
}
